/**
 * @file DeliveryPackageSuggestion.tsx
 * @description Расчёт места по товарам: кнопка и итог (phase19.12 шаг 5).
 *
 * Одна кнопка на оба сценария — первичное заполнение и возврат расчётных
 * значений после ручной правки. Если в товарах чего-то нет, это написано
 * прямым текстом: менеджер сразу знает, что править в карточке товара.
 */

import { Calculator } from "lucide-react";

import { Button } from "../ui/button";
import { formatDims, formatWeight } from "../../lib/delivery-quote";
import { canSuggestPackage, type PackageSuggestion } from "../../lib/package-suggestion";

interface Props {
  suggestion: PackageSuggestion;
  disabled?: boolean;
  onApply: () => void;
}

export function DeliveryPackageSuggestion({ suggestion, disabled, onApply }: Props) {
  const can = canSuggestPackage(suggestion);
  const summary = [
    suggestion.weightG !== null ? formatWeight(suggestion.weightG) : "вес неизвестен",
    formatDims(suggestion.lengthMm, suggestion.widthMm, suggestion.heightMm) ??
      "габариты неизвестны",
  ].join(" · ");

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <Button
          variant="outline"
          size="sm"
          className="min-h-11"
          disabled={disabled || !can}
          title={
            can
              ? "Вес и коробка возьмутся из карточек товаров"
              : "У части товаров не заполнены вес или габариты"
          }
          onClick={onApply}
        >
          <Calculator className="mr-1 size-4" />
          Рассчитать по товарам
        </Button>
        <span className="text-xs text-on-surface-variant">По товарам: {summary}</span>
      </div>

      {can && suggestion.missingDims.length > 0 ? (
        <p className="text-xs text-warning">
          Габариты не заполнены у товаров: {suggestion.missingDims.join(", ")} — размеры
          коробки оставим как есть.
        </p>
      ) : null}
      {can && suggestion.missingWeight.length > 0 ? (
        <p className="text-xs text-warning">
          Вес не заполнен у товаров: {suggestion.missingWeight.join(", ")} — впишите вес
          места вручную.
        </p>
      ) : null}
      {!can ? (
        <p className="text-xs text-warning">
          В товарах нет ни веса, ни габаритов — заполните их в карточках товаров или
          укажите параметры места вручную.
        </p>
      ) : null}
    </div>
  );
}
